/**
 * Environment variables for a project's edge functions.
 *
 * Two things this panel has to communicate, neither of which is obvious
 * from a list of names:
 *
 *  - The value cannot be read back. Not by anyone, not from here. A person
 *    who assumes otherwise will not write it down.
 *  - Setting one redeploys the project's functions, and any that fail are
 *    still running the OLD value. That is displayed in full, by name, and
 *    does not disappear on the next render - a rotation that half-worked is
 *    the failure this panel exists to make visible.
 */

import { useState } from 'react'
import { PermissionButton } from './Permissions.js'
import { useApi, useAsync } from '../lib/useApi.js'
import { ApiError } from '../lib/api.js'
import type { SecretWrite } from '../lib/api.js'
import { Empty, ErrorBox, Loading, relative } from './ui.js'

const NAME = /^[A-Z][A-Z0-9_]{0,63}$/

export default function Secrets({ projectId }: { projectId: string }) {
  const api = useApi()
  const secrets = useAsync(() => api.listSecrets(projectId), [api, projectId])

  const [name, setName] = useState('')
  const [value, setValue] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastWrite, setLastWrite] = useState<(SecretWrite & { name: string; removed?: boolean }) | null>(null)

  const nameOk = NAME.test(name)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const result = await api.setSecret(projectId, name, value)
      setLastWrite({ ...result, name })
      setName('')
      // Cleared immediately: a value left in a text input survives a
      // screenshot, a screen-share, and the browser's own form restore.
      setValue('')
      secrets.reload()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not set that secret.')
    } finally {
      setBusy(false)
    }
  }

  async function remove(secretName: string) {
    if (busy) return
    if (
      !confirm(
        `Remove ${secretName}? It cannot be recovered, and functions using it will be redeployed without it.`,
      )
    )
      return
    setError(null)
    setBusy(true)
    try {
      const result = await api.deleteSecret(projectId, secretName)
      setLastWrite({ ...result, name: secretName, removed: true })
      secrets.reload()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not remove that secret.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <h2>Environment variables</h2>

      <form className="card" onSubmit={submit} style={{ padding: 16, marginBottom: 16 }}>
        {error && <ErrorBox>{error}</ErrorBox>}
        <div className="form-row">
          <div className="field" style={{ width: 260, marginBottom: 0 }}>
            <label htmlFor="s-name">Name</label>
            <input
              id="s-name"
              value={name}
              onChange={(e) => setName(e.target.value.toUpperCase())}
              placeholder="STRIPE_SECRET_KEY"
              maxLength={64}
              required
            />
          </div>
          <div className="field" style={{ flex: 1, marginBottom: 0 }}>
            <label htmlFor="s-value">Value</label>
            <input
              id="s-value"
              type="password"
              autoComplete="off"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              maxLength={5120}
              required
            />
          </div>
          <PermissionButton
            permission="apikey:create"
            className="btn btn-primary"
            disabled={busy || !nameOk || value === ''}
          >
            {busy ? 'Saving…' : 'Set'}
          </PermissionButton>
        </div>
        {name !== '' && !nameOk && (
          <p className="sub small" style={{ marginTop: 10 }}>
            A-Z, 0-9 and underscores, starting with a letter.
          </p>
        )}
        <p className="sub small" style={{ marginTop: 10 }}>
          Your functions read these as <code>env.NAME</code>. Stored encrypted with a key derived for this
          project — <strong>the value can never be read back</strong>, only replaced. Setting one redeploys
          every function in the project, because bindings are fixed when a function is uploaded.
        </p>
      </form>

      {lastWrite && (
        <div className={lastWrite.stale.length > 0 ? 'notice notice-danger' : 'notice'}>
          {lastWrite.stale.length > 0 ? (
            <>
              <strong>
                {lastWrite.name} was saved, but {lastWrite.stale.length} function
                {lastWrite.stale.length === 1 ? '' : 's'} could not be redeployed.
              </strong>
              <p className="sub small" style={{ margin: '6px 0' }}>
                {lastWrite.removed
                  ? 'They are still running with the value you just removed.'
                  : 'They are still running with the previous value.'}
              </p>
              <ul className="sub small" style={{ margin: 0, paddingLeft: 18 }}>
                {lastWrite.stale.map((s) => (
                  <li key={s.name}>
                    <code>{s.name}</code> — {s.message}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <strong>
              {lastWrite.name} {lastWrite.removed ? 'removed' : 'set'}
              {lastWrite.redeployed.length > 0 ? ` — redeployed ${lastWrite.redeployed.join(', ')}.` : '.'}
            </strong>
          )}
          <div>
            <button className="btn" onClick={() => setLastWrite(null)}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="card">
        {secrets.loading ? (
          <Loading />
        ) : secrets.error ? (
          <ErrorBox retry={secrets.reload}>{secrets.error}</ErrorBox>
        ) : !secrets.data?.secrets.length ? (
          <Empty>No environment variables set.</Empty>
        ) : (
          secrets.data.secrets.map((s) => (
            <div className="row" key={s.name}>
              <div className="row-main">
                <div className="row-title mono">{s.name}</div>
                <div className="row-sub">
                  {/* A hint, not a prefix of the value. Short values show
                      only a length, because four characters of a short
                      secret is most of it. */}
                  {s.hint} · set {relative(s.updatedAt)}
                </div>
              </div>
              <PermissionButton
                permission="apikey:create"
                className="btn btn-danger"
                disabled={busy}
                onClick={() => remove(s.name)}
              >
                Remove
              </PermissionButton>
            </div>
          ))
        )}
      </div>
    </>
  )
}
